import { ds } from '@/components/lib/design.system';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { withOpacity } from '@/styles/styles';
import { EntryRulesOutput, GameMode, ProjectionType } from '@/types/api.generated';
import { TFunction } from 'i18next';

import { BetValidationData } from '../api/types';

export const MULTIPLIER_BG_COLOR = withOpacity(ds.colorValue.background.brand.emphasis, 0.16);

export const getGameModeError = (
    betslip: BetslipPick[],
    allEntryRules: EntryRulesOutput[],
    data: BetValidationData | undefined
) => {
    if (!data) {
        return { error: 'Not available at this time', disabled: true };
    }

    const rules = allEntryRules.find(it => it.id === data.entryRulesId);
    if (!rules) {
        return { error: 'Not available at this time', disabled: true };
    }

    const maxNumberOfPicks = data.maxNumberOfPicks ?? rules.maxNumberOfPicks;
    const minNumberOfPicks = data.minNumberOfPicks ?? rules.minNumberOfPicks;

    const errors = data.errors;
    if (errors.length === 0) {
        return { error: undefined, disabled: false };
    }

    if (betslip.length > maxNumberOfPicks) {
        return { error: `Max ${maxNumberOfPicks} picks allowed`, disabled: true };
    }

    if (betslip.length < minNumberOfPicks) {
        return { error: `Minimum ${minNumberOfPicks} picks needed`, disabled: true };
    }

    let error: { error: string; disabled: boolean } | undefined;

    //Find if a specific projection type has exceeded it's max picks
    Object.values(ProjectionType)
        .filter(it => it !== ProjectionType.Regular)
        //For each non-regular projection type, checked that it's number has not been exceeded
        .forEach(projctionType => {
            const maxPicksForType = data.maxNumberOfPicksForType?.find(it => it.type === projctionType)?.maxPicks;
            const picksForType = betslip.filter(it => it.projection.type === projctionType).length;
            const pickTypeLabel = getPickTypeLabel(projctionType);
            if (maxPicksForType !== undefined && picksForType > maxPicksForType) {
                error = { error: `Max ${maxPicksForType} ${pickTypeLabel} picks allowed`, disabled: true };
            }
            if (maxPicksForType === 0 && picksForType > 0) {
                error = { error: `Not available with ${pickTypeLabel} picks`, disabled: true };
            }
        });

    if (error) {
        return error;
    }

    //If we can't determine the error return the first error message from our BE
    return {
        error: errors[0]?.message ?? 'Not available at this time',
        disabled: false,
    };
};

const pickTypeLabels: Record<ProjectionType, string> = {
    [ProjectionType.Boosted]: 'Rocket Lvl 1',
    [ProjectionType.MiniBoosted]: 'Rocket Lvl 2',
    [ProjectionType.SuperBoosted]: 'Rocket Lvl 3',
    [ProjectionType.Boosted4]: 'Rocket Lvl 4',

    [ProjectionType.Special]: 'Special',

    [ProjectionType.FreePick]: 'Free',

    [ProjectionType.Edge1]: 'Edge Lvl 1',
    [ProjectionType.Edge2]: 'Edge Lvl 2',
    [ProjectionType.Edge3]: 'Edge Lvl 3',
    [ProjectionType.Edge4]: 'Edge Lvl 4',

    [ProjectionType.Anchor]: 'Anchor',
    [ProjectionType.Regular]: 'Regular',
    [ProjectionType.Nuke]: 'Nuke',
};

export const getPickTypeLabel = (type: ProjectionType) => {
    return pickTypeLabels[type];
};

export const getGameModeDetails = (gameMode: GameMode, allEntryRules: EntryRulesOutput[]) => {
    switch (gameMode) {
        case GameMode.Perfect:
            const perfectRules = allEntryRules.find(rule => rule.gameMode === GameMode.Perfect);
            return {
                // show "Perfect Play" for all game types, including P2P
                name: 'Perfect',
                description: perfectRules?.description ?? '',
            };
        case GameMode.Dynamic:
            const dynamicRules = allEntryRules.find(rule => rule.gameMode === GameMode.Dynamic);
            return {
                // show "Flex Play" for all game types, including P2P
                name: 'Flex',
                description: dynamicRules?.description ?? '',
            };
        default:
            return {
                name: '',
                description: '',
            };
    }
};

export const getPickslipTabs = (entryRules: EntryRulesOutput[]) => {
    const ORDER = [GameMode.Perfect.toString(), GameMode.Dynamic.toString()];
    return entryRules
        .map(({ gameMode }) => {
            const { name: title } = getGameModeDetails(gameMode, entryRules);
            return {
                id: gameMode.toString(),
                title: title,
            };
        })
        .sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));
};

export const getGameModeSubtitle = ({
    gameMode,
    p2pGameType,
    perfectModeError,
    dynamicModeError,
    betslip,
    lowRange,
    highRange,
    t,
}: {
    gameMode: GameMode;
    p2pGameType: boolean;
    perfectModeError: string | undefined;
    dynamicModeError: string | undefined;
    betslip: BetslipPick[];
    lowRange: string;
    highRange: string;
    t: TFunction<['common', 'p2p', 'betslip_pickem'], undefined>;
}) => {
    const betslipLength = betslip.length;
    if (gameMode === GameMode.Perfect) {
        return (
            perfectModeError ??
            (p2pGameType
                ? t('p2p:get_first_place', { betslipLength })
                : t('betslip_pickem:all_to_win', { betslipLength }))
        );
    }
    if (gameMode === GameMode.Dynamic) {
        return (
            dynamicModeError ??
            (p2pGameType
                ? t('p2p:get_first_place_dynamic', { least: lowRange, total: highRange })
                : t('betslip_pickem:at_least_ranges', { lowRange, highRange }))
        );
    }
    return '';
};
